/**
 * Floating ✨ AI Studio launcher (iter_461).
 *
 * Pinned bottom-right on every page. Single tap drops a mini menu
 * with the three AI tools. Hidden on the AI Studio pages themselves
 * (already there) and on auth pages so we don't crowd those flows.
 */
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sparkles, X, Headphones, Image as ImageIcon, Mic } from 'lucide-react';

const HIDE_ROUTES = ['/login', '/signup', '/register', '/verify-email', '/ai-studio'];

const AIStudioLauncher = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Hide on the studio pages themselves + auth flows
  const hide = HIDE_ROUTES.some((p) => location.pathname.startsWith(p));
  if (hide) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[900]" data-testid="ai-studio-launcher">
      {open && (
        <div
          className="mb-3 flex flex-col gap-2 items-end"
          data-testid="ai-studio-launcher-menu"
        >
          <LauncherItem
            to="/ai-studio/ar-scout"
            label="A&R Scout"
            icon={Headphones}
            colors="from-[#ff4444] to-[#FFDA33]"
            testId="launcher-ar-scout"
            onClick={() => setOpen(false)}
          />
          <LauncherItem
            to="/ai-studio/cover-art"
            label="Cover Art"
            icon={ImageIcon}
            colors="from-[#ff6b9d] to-[#9b59b6]"
            testId="launcher-cover-art"
            onClick={() => setOpen(false)}
          />
          <LauncherItem
            to="/ai-studio/songwriter"
            label="Songwriter"
            icon={Mic}
            colors="from-[#00ccff] to-[#00ff88]"
            testId="launcher-songwriter"
            onClick={() => setOpen(false)}
          />
          <Link
            to="/ai-studio"
            onClick={() => setOpen(false)}
            className="text-[10px] font-mono uppercase tracking-wider text-[#888] hover:text-white px-2"
            data-testid="launcher-hub-link"
          >
            See all →
          </Link>
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#ff6b9d] via-[#FFDA33] to-[#00ccff] shadow-2xl hover:scale-110 transition-transform flex items-center justify-center"
        aria-label="Open AI Studio"
        data-testid="ai-studio-launcher-btn"
        style={{ boxShadow: '0 8px 28px rgba(255,107,157,0.4)' }}
      >
        {open ? <X size={22} className="text-black" /> : <Sparkles size={22} className="text-black" />}
        {!open && (
          <span className="absolute -top-1 -right-1 text-[8px] uppercase font-mono tracking-wider bg-black text-[#FFDA33] border border-[#FFDA33]/60 rounded-full px-1.5 py-0.5">AI</span>
        )}
      </button>
    </div>
  );
};

const LauncherItem = ({ to, label, icon: Icon, colors, testId, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r ${colors} text-black font-['Chakra_Petch'] uppercase text-xs shadow-xl hover:scale-105 transition-transform`}
    data-testid={testId}
  >
    <Icon size={14} />
    <span>{label}</span>
  </Link>
);

export default AIStudioLauncher;
